import {
  beginNodeHistoryGesture,
  completeNodeHistoryGesture,
  recordNodeHistory,
  redoNodeHistory,
  shouldRecordNodeChanges,
  undoNodeHistory,
} from '@/components/diagrams/FlowChart/utils/nodeHistory';
import { createGroupHistorySupplement } from '@/modules/graphViz/graphVizGroup/groupHistory';
import {
  normalizeGroupMembership,
  projectVisualizationGroups,
} from '@/modules/graphViz/graphVizGroup/grouping';
import { UnreachableError } from '@/utils/errors';

import {
  autoPositionAction,
  closeSummaryPanelAction,
  completeInitializationAction,
  edgeMouseEnterAction,
  edgeMouseLeaveAction,
  initializeNodePositionsAction,
  nodeMouseEnterAction,
  nodeMouseLeaveAction,
  nodesChangedAction,
  openSummaryPanelAction,
  reactFlowNodeMeasurementEventAction,
} from '../actions';
import { addTextNoteAction } from '../actions/addTextNote.action';
import { applyTileNotesAction } from '../actions/applyTileNotes.action';
import { bulkTileSizeChangeAction } from '../actions/bulkTileSizeChange.action';
import { deleteTextNoteAction } from '../actions/deleteTextNote.action';
import { setOpacityOnAction } from '../actions/setOpacityOn.action';
import { setRenderWhiteLabeledAction } from '../actions/setRenderWhiteLabeled.action';
import { updateTextNoteAction } from '../actions/updateTextNote.action';
import { updateTileConfigAction } from '../actions/updateTileConfig.action';
import { EntityDiagramAction, EntityDiagramReducer } from '../types';

export const entityDiagramReducer: EntityDiagramReducer = (
  state,
  action,
  queueEffect
) => {
  const groupHistorySupplement = createGroupHistorySupplement<typeof state>();
  const recordHistory = (nextState: typeof state) =>
    recordNodeHistory(state, nextState, action.type);

  switch (action.type) {
    case 'EDGE_MOUSE_ENTER':
      return edgeMouseEnterAction(state, action, queueEffect);
    case 'EDGE_MOUSE_LEAVE':
      return edgeMouseLeaveAction(state, action, queueEffect);
    case 'NODE_MOUSE_ENTER':
      return nodeMouseEnterAction(state, action, queueEffect);
    case 'NODE_MOUSE_LEAVE':
      return nodeMouseLeaveAction(state, action, queueEffect);
    case 'INITIALIZE_NODE_POSITIONS':
      return initializeNodePositionsAction(state, action, queueEffect);
    case 'NODES_CHANGED': {
      const nextState = nodesChangedAction(state, action, queueEffect);
      return action.isInteractive &&
        shouldRecordNodeChanges(action.changes, state.nodeHistory)
        ? recordHistory(nextState)
        : nextState;
    }
    case 'AUTO_POSITION':
      return recordHistory(autoPositionAction(state, action, queueEffect));
    case 'COMPLETE_INITIALIZATION':
      return completeInitializationAction(state, action, queueEffect);
    case 'REACTFLOW_NODE_MEASUREMENT_EVENT':
      return reactFlowNodeMeasurementEventAction(state, action, queueEffect);
    case 'OPEN_SUMMARY_PANEL':
      return openSummaryPanelAction(state, action, queueEffect);
    case 'CLOSE_SUMMARY_PANEL':
      return closeSummaryPanelAction(state, action, queueEffect);
    case 'START_EXPORT':
      return {
        ...state,
        isExporting: true,
      };
    case 'END_EXPORT':
      return {
        ...state,
        isExporting: false,
      };
    case 'SET_OPACITY_ON':
      return setOpacityOnAction(state, action, queueEffect);
    case 'UPDATE_ENTITY_DIAGRAM':
      return {
        ...state,
        entityDiagramId: action.entityDiagramId,
        nodeConfigurations: action.nodeConfigurations,
      };
    case 'SET_RENDER_WHITE_LABELED':
      return setRenderWhiteLabeledAction(state, action, queueEffect);
    case 'ADD_TEXT_NOTE':
      return recordHistory(addTextNoteAction(state, action, queueEffect));
    case 'DELETE_TEXT_NOTE':
      return recordHistory(deleteTextNoteAction(state, action, queueEffect));
    case 'UPDATE_TEXT_NOTE':
      return updateTextNoteAction(state, action, queueEffect);
    case 'UPDATE_TILE_CONFIG':
      return recordHistory(updateTileConfigAction(state, action, queueEffect));
    case 'APPLY_TILE_NOTES':
      return recordHistory(applyTileNotesAction(state, action, queueEffect));
    case 'BULK_TILE_SIZE_CHANGE':
      return recordHistory(
        bulkTileSizeChangeAction(state, action, queueEffect)
      );
    case 'NODE_DRAG_START':
      return beginNodeHistoryGesture(state, 'drag');
    case 'NODE_DRAG_STOP':
      return completeNodeHistoryGesture(state, 'drag', action.type);
    case 'NODE_RESIZE_START':
      return beginNodeHistoryGesture(state, 'resize');
    case 'NODE_RESIZE_END':
      return completeNodeHistoryGesture(state, 'resize', action.type);
    case 'UNDO_HISTORY': {
      const lastEntry =
        state.nodeHistory.past[state.nodeHistory.past.length - 1];
      if (lastEntry?.actionType === 'AUTO_POSITION') {
        queueEffect({
          type: 'fitViewEffect',
          presentationMode: state.presentationMode,
        });
      }
      queueEffect({ type: 'recomputeSectionWidthsEffect' });
      return undoNodeHistory(state, groupHistorySupplement);
    }
    case 'REDO_HISTORY':
      queueEffect({ type: 'recomputeSectionWidthsEffect' });
      return redoNodeHistory(state, groupHistorySupplement);
    case 'SET_VISUALIZATION_GROUPS': {
      const groups = normalizeGroupMembership(action.groups);
      const projected = projectVisualizationGroups({
        nodes: state.nodes,
        edges: state.baseEdges,
        groups,
        sectioned: true,
        hideEntitiesListInGroups: state.hideEntitiesListInGroups,
      });
      return recordNodeHistory(
        state,
        // Component type lives on the node, where toolbar edits also update it.
        {
          ...state,
          ...projected,
          visualizationGroups: groups.map(
            ({ componentType: _componentType, ...group }) => group
          ),
        },
        action.type,
        action.mergePreviousDrag,
        groupHistorySupplement
      );
    }
    case 'SET_HIDE_GROUP_CONTENTS': {
      const projected = projectVisualizationGroups({
        nodes: state.nodes,
        edges: state.baseEdges,
        groups: state.visualizationGroups,
        sectioned: true,
        hideEntitiesListInGroups: action.value,
      });
      return recordNodeHistory(
        state,
        {
          ...state,
          ...projected,
          hideEntitiesListInGroups: action.value,
        },
        action.type,
        false,
        groupHistorySupplement
      );
    }
    default:
      throw new UnreachableError({
        case: (action as EntityDiagramAction).type as never,
        message: `Unknown action ${(action as EntityDiagramAction).type}`,
      });
  }
};
